import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { TrendingUp, ArrowRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import TaxLiabilityForm from '@/components/TaxLiabilityForm';

// Animation component for text appearing line by line
const AnimatedParagraph = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <p
      className={`text-lg text-muted-foreground mb-6 leading-relaxed text-justify transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </p>
  );
};

const TaxLiabilityPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  return (
    <div className="min-h-screen">
      <Header />
      <section className="pt-32 pb-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-full mb-6">
              <TrendingUp className="h-8 w-8 text-gold-light" />
            </div>
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
              Zero your current IRS Tax Bill
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Settle your IRS bill using lawful methods including bills of exchange and negotiable instruments
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <AnimatedParagraph delay={200}>
              We settle your IRS tax bill using lawful methods grounded in bills of exchange, negotiable instruments, and Title 26 of the Uniform Commercial Code. Our process leverages a deep understanding of these commercial instruments to lawfully discharge your tax debt.
            </AnimatedParagraph>
            
            <AnimatedParagraph delay={600}>
              Our comprehensive approach ensures you understand the legal foundation and commercial mechanisms used to resolve your IRS obligations while maintaining full compliance with applicable law and commercial codes.
            </AnimatedParagraph>

          {/* Call to Action */}
          <div className="bg-gradient-to-br from-primary/10 to-gold/20 rounded-2xl p-8 md:p-12 text-center border border-gold/30 mt-12">
            <h2 className="font-rockwell text-3xl md:text-4xl font-semibold text-primary mb-6">
              Ready to Zero your Account?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
              Start your journey to resolving your IRS obligations. Our expert team will guide you through the lawful process of settling your tax bill using commercial instruments and UCC principles.
            </p>
            
            <Collapsible open={isFormOpen} onOpenChange={setIsFormOpen}>
              <CollapsibleTrigger asChild>
                <div className="flex justify-center">
                  <Button 
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3 text-lg"
                  >
                    Get Started
                    <ChevronDown className={`ml-2 h-5 w-5 transition-transform ${isFormOpen ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-8">
                <TaxLiabilityForm onSuccess={() => setIsFormOpen(false)} />
              </CollapsibleContent>
            </Collapsible>
          </div>
          </div>
        </div>
      </section>

      {/* Educate Yourself Card */}
      <section className="pb-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <AnimatedParagraph delay={2600}>
            <Link to="/category/irs-taxes/videos" className="block">
              <div className="p-8 bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl border border-gold/20 hover:border-gold/40 transition-all duration-500 ease-out hover:shadow-lg hover:shadow-gold/20">
                <div className="mb-4">
                  <div className="w-12 h-1 bg-primary mb-6"></div>
                </div>
                <h3 className="font-rockwell text-2xl font-semibold text-primary mb-4 leading-tight">
                  Wanna do for self for free?
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-6 text-sm">
                  We are committed to protecting the constitutional rights of all men and women on the land---and because of this, we make all of our resources and processes completely free so that you are empowered to act on your own without needing anyone or anything or even money for that matter. We give it all for free and if you want to do it that way just click the button below.
                </p>
                <div className="flex justify-start">
                  <Button 
                    variant="link"
                    className="text-primary hover:text-gold p-0 h-auto text-sm font-medium flex items-center gap-1 pointer-events-none"
                  >
                    Educate Myself and Act <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Link>
          </AnimatedParagraph>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default TaxLiabilityPage;